import React, { useState } from 'react';
import {
  Calculator, AlertTriangle, Info, FileText, Zap,
  Settings, Thermometer, Shield, Download, ChevronRight
} from 'lucide-react';
import {
  calculatePrecisionPullForce,
  calculateRequiredMagnets,
  compareGrades,
  recommendOptimalGrade,
  generateTechnicalReport,
  NEO_GRADES,
  type PrecisionMagnetInput
} from '@/utils/magnetCalculatorPrecision';

export const PrecisionMagnetCalculator: React.FC = () => {
  // Stan dla precyzyjnego kalkulatora
  const [precisionInput, setPrecisionInput] = useState<PrecisionMagnetInput>({
    shape: 'disc',
    D: 20,
    L: 5,
    grade: 'N42',
    T: 20,
    airGap: 0,
    steelThickness: 3,
    orientation: 'normal',
    surface: 'milled',
    safetyFactor: 2
  });

  const [precisionResult, setPrecisionResult] = useState<ReturnType<typeof calculatePrecisionPullForce> | null>(null);
  const [loadWeight, setLoadWeight] = useState(5); // kg
  const [showComparison, setShowComparison] = useState(false);
  const [comparisonResults, setComparisonResults] = useState<ReturnType<typeof compareGrades> | null>(null);
  const [showTechnicalReport, setShowTechnicalReport] = useState(false);

  // Obsługa zmian w formularzu
  const handlePrecisionInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setPrecisionInput(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  // Obliczenie siły precyzyjnej
  const calculatePrecision = () => {
    const result = calculatePrecisionPullForce(precisionInput);
    setPrecisionResult(result);
    
    // Automatyczne porównanie gatunków
    if (showComparison) {
      const grades = ['N35', 'N42', 'N45', 'N52', 'N42M', 'N42H', 'N42SH'];
      const comparison = compareGrades(
        {
          shape: precisionInput.shape,
          D: precisionInput.D,
          L: precisionInput.L,
          a: precisionInput.a,
          b: precisionInput.b,
          T: precisionInput.T,
          airGap: precisionInput.airGap,
          steelThickness: precisionInput.steelThickness,
          orientation: precisionInput.orientation,
          surface: precisionInput.surface,
          safetyFactor: precisionInput.safetyFactor
        },
        grades
      );
      setComparisonResults(comparison);
    }
  };

  // Generowanie raportu PDF
  const downloadTechnicalReport = () => {
    if (!precisionResult) return;
    
    const report = generateTechnicalReport(precisionInput, precisionResult);
    const blob = new Blob([report], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `raport_techniczny_magnes_${precisionInput.grade}_${new Date().getTime()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Nagłówek */}
      <div className="bg-zinc-700 rounded-lg p-6">
        <h3 className="text-xl font-semibold mb-4 text-white flex items-center gap-2">
          <Zap className="w-5 h-5 text-orange-500" />
          Precyzyjny kalkulator siły magnesów - zgodny z teorią Maxwella
        </h3>
        <p className="text-sm text-gray-400">
          Dokładne obliczenia oparte na rzeczywistych danych technicznych i wzorach fizycznych.
          Uwzględnia temperaturę, szczelinę powietrzną, grubość stali i stan powierzchni.
        </p>
      </div>

      {/* Formularz wejściowy */}
      <div className="bg-zinc-700 rounded-lg p-6">
        <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Parametry magnesu i warunków pracy
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Kształt i wymiary */}
          <div className="space-y-3">
            <h5 className="text-sm font-medium text-gray-400">Geometria</h5>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Kształt</label>
              <select
                name="shape"
                value={precisionInput.shape}
                onChange={handlePrecisionInputChange}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
              >
                <option value="disc">Dysk</option>
                <option value="block">Blok</option>
              </select>
            </div>
            
            {precisionInput.shape === 'disc' ? (
              <>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Średnica D (mm)</label>
                  <input
                    type="number"
                    name="D"
                    value={precisionInput.D}
                    onChange={handlePrecisionInputChange}
                    min="1"
                    max="100"
                    step="0.5"
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Szerokość a (mm)</label>
                  <input
                    type="number"
                    name="a"
                    value={precisionInput.a || 20}
                    onChange={handlePrecisionInputChange}
                    min="1"
                    max="100"
                    step="0.5"
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Długość b (mm)</label>
                  <input
                    type="number"
                    name="b"
                    value={precisionInput.b || 20}
                    onChange={handlePrecisionInputChange}
                    min="1"
                    max="100"
                    step="0.5"
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                  />
                </div>
              </>
            )}
            
            <div>
              <label className="block text-sm text-gray-300 mb-1">Grubość L (mm)</label>
              <input
                type="number"
                name="L"
                value={precisionInput.L}
                onChange={handlePrecisionInputChange}
                min="0.5"
                max="50"
                step="0.5"
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Gatunek i temperatura */}
          <div className="space-y-3">
            <h5 className="text-sm font-medium text-gray-400">Materiał</h5>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Gatunek magnesu</label>
              <select
                name="grade"
                value={precisionInput.grade}
                onChange={handlePrecisionInputChange}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
              >
                <optgroup label="Standard (do 80°C)">
                  {Object.keys(NEO_GRADES).filter(g => !g.includes('M') && !g.includes('H')).map(grade => (
                    <option key={grade} value={grade}>
                      {grade} (Br: {NEO_GRADES[grade].Br20_T}T, max {NEO_GRADES[grade].Tmax_C}°C)
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Średnia temp. (do 100°C)">
                  {Object.keys(NEO_GRADES).filter(g => g.includes('M') && !g.includes('H')).map(grade => (
                    <option key={grade} value={grade}>
                      {grade} (Br: {NEO_GRADES[grade].Br20_T}T, max {NEO_GRADES[grade].Tmax_C}°C)
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Wysoka temp. (do 120°C)">
                  {Object.keys(NEO_GRADES).filter(g => g.includes('H') && !g.includes('S') && !g.includes('U')).map(grade => (
                    <option key={grade} value={grade}>
                      {grade} (Br: {NEO_GRADES[grade].Br20_T}T, max {NEO_GRADES[grade].Tmax_C}°C)
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Bardzo wysoka temp. (do 150°C)">
                  {Object.keys(NEO_GRADES).filter(g => g.includes('SH')).map(grade => (
                    <option key={grade} value={grade}>
                      {grade} (Br: {NEO_GRADES[grade].Br20_T}T, max {NEO_GRADES[grade].Tmax_C}°C)
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Ultra wysoka temp. (do 180°C)">
                  {Object.keys(NEO_GRADES).filter(g => g.includes('UH')).map(grade => (
                    <option key={grade} value={grade}>
                      {grade} (Br: {NEO_GRADES[grade].Br20_T}T, max {NEO_GRADES[grade].Tmax_C}°C)
                    </option>
                  ))}
                </optgroup>
              </select>
            </div>
            
            <div>
              <label className="block text-sm text-gray-300 mb-1">
                <Thermometer className="w-3 h-3 inline" /> Temperatura pracy (°C)
              </label>
              <input
                type="number"
                name="T"
                value={precisionInput.T}
                onChange={handlePrecisionInputChange}
                min="-40"
                max="200"
                step="5"
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
              />
              {NEO_GRADES[precisionInput.grade] && (
                <p className="text-xs text-gray-400 mt-1">
                  Max dla {precisionInput.grade}: {NEO_GRADES[precisionInput.grade].Tmax_C}°C
                </p>
              )}
            </div>
          </div>

          {/* Warunki montażu */}
          <div className="space-y-3">
            <h5 className="text-sm font-medium text-gray-400">Warunki montażu</h5>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Szczelina powietrzna (mm)</label>
              <input
                type="number"
                name="airGap"
                value={precisionInput.airGap}
                onChange={handlePrecisionInputChange}
                min="0"
                max="10"
                step="0.1"
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
              />
            </div>
            
            <div>
              <label className="block text-sm text-gray-300 mb-1">Grubość stali (mm)</label>
              <input
                type="number"
                name="steelThickness"
                value={precisionInput.steelThickness}
                onChange={handlePrecisionInputChange}
                min="0.5"
                max="20"
                step="0.5"
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
              />
            </div>
            
            <div>
              <label className="block text-sm text-gray-300 mb-1">Stan powierzchni</label>
              <select
                name="surface"
                value={precisionInput.surface}
                onChange={handlePrecisionInputChange}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
              >
                <option value="milled">Frezowana (100%)</option>
                <option value="painted">Malowana (85%)</option>
                <option value="rusty">Zardzewiała (70%)</option>
              </select>
            </div>
          </div>

          {/* Orientacja i bezpieczeństwo */}
          <div className="space-y-3">
            <h5 className="text-sm font-medium text-gray-400">Zastosowanie</h5>
            <div>
              <label className="block text-sm text-gray-300 mb-1">Orientacja siły</label>
              <select
                name="orientation"
                value={precisionInput.orientation}
                onChange={handlePrecisionInputChange}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
              >
                <option value="normal">Prostopadła (odrywanie)</option>
                <option value="shear">Ścinanie (przesuwanie)</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm text-gray-300 mb-1">
                <Shield className="w-3 h-3 inline" /> Współczynnik bezpieczeństwa
              </label>
              <input
                type="number"
                name="safetyFactor"
                value={precisionInput.safetyFactor}
                onChange={handlePrecisionInputChange}
                min="1.5"
                max="5"
                step="0.5"
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
              />
            </div>
            
            <div>
              <label className="block text-sm text-gray-300 mb-1">Obciążenie (kg)</label>
              <input
                type="number"
                value={loadWeight}
                onChange={(e) => setLoadWeight(parseFloat(e.target.value) || 0)}
                min="0.1"
                max="1000"
                step="0.5"
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
              />
            </div>
          </div>
        </div>

        {/* Przyciski akcji */}
        <div className="mt-6 flex gap-3">
          <button
            onClick={calculatePrecision}
            className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all flex items-center justify-center gap-2"
          >
            <Calculator className="w-4 h-4" />
            Oblicz precyzyjnie
          </button>
          
          <button
            onClick={() => setShowComparison(!showComparison)}
            className={`px-4 py-2 ${showComparison ? 'bg-purple-600' : 'bg-zinc-600'} text-white rounded-lg hover:opacity-90 transition-all flex items-center gap-2`}
          >
            <ChevronRight className="w-4 h-4" />
            Porównaj gatunki
          </button>
          
          {precisionResult && (
            <button
              onClick={downloadTechnicalReport}
              className="px-4 py-2 bg-zinc-600 text-white rounded-lg hover:bg-zinc-500 transition-all flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Raport techniczny
            </button>
          )}
        </div>
      </div>

      {/* Wyniki precyzyjne */}
      {precisionResult && (
        <div className="bg-zinc-700 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-orange-500" />
            Wyniki obliczeń precyzyjnych
          </h4>

          {/* Główne wyniki */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-zinc-800 rounded-lg p-4">
              <h5 className="text-sm text-gray-400 mb-2">Siła normalna</h5>
              <p className="text-2xl font-bold text-green-400">
                {precisionResult.F_normal.toFixed(1)} N
              </p>
              <p className="text-sm text-gray-300 mt-1">
                {(precisionResult.F_normal / 9.81).toFixed(2)} kg
              </p>
            </div>
            
            <div className="bg-zinc-800 rounded-lg p-4">
              <h5 className="text-sm text-gray-400 mb-2">Siła ścinania</h5>
              <p className="text-2xl font-bold text-blue-400">
                {precisionResult.F_shear.toFixed(1)} N
              </p>
              <p className="text-sm text-gray-300 mt-1">
                {(precisionResult.F_shear / 9.81).toFixed(2)} kg
              </p>
            </div>
            
            <div className="bg-zinc-800 rounded-lg p-4">
              <h5 className="text-sm text-gray-400 mb-2">Siła projektowa</h5>
              <p className="text-2xl font-bold text-orange-400">
                {precisionResult.F_design.toFixed(1)} N
              </p>
              <p className="text-sm text-gray-300 mt-1">
                {(precisionResult.F_design / 9.81).toFixed(2)} kg
              </p>
            </div>
            
            <div className="bg-zinc-800 rounded-lg p-4">
              <h5 className="text-sm text-gray-400 mb-2">Indukcja B_gap</h5>
              <p className="text-2xl font-bold text-purple-400">
                {(precisionResult.B_gap * 1000).toFixed(0)} mT
              </p>
              <p className="text-sm text-gray-300 mt-1">
                {precisionResult.B_gap.toFixed(3)} T
              </p>
            </div>
          </div>

          {/* Szczegóły techniczne */}
          <div className="bg-zinc-800 rounded-lg p-4 mb-6">
            <h5 className="text-sm font-medium text-gray-400 mb-3">Szczegóły obliczeń</h5>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
              <div>
                <span className="text-gray-400">Br(T):</span>
                <span className="text-white ml-2">{precisionResult.details.Br_T.toFixed(3)} T</span>
              </div>
              <div>
                <span className="text-gray-400">k_geo:</span>
                <span className="text-white ml-2">{precisionResult.details.k_geo.toFixed(3)}</span>
              </div>
              <div>
                <span className="text-gray-400">f_gap:</span>
                <span className="text-white ml-2">{precisionResult.details.f_gap.toFixed(3)}</span>
              </div>
              <div>
                <span className="text-gray-400">f_steel:</span>
                <span className="text-white ml-2">{precisionResult.details.f_steel.toFixed(3)}</span>
              </div>
              <div>
                <span className="text-gray-400">f_surface:</span>
                <span className="text-white ml-2">{precisionResult.details.f_surface.toFixed(2)}</span>
              </div>
              <div>
                <span className="text-gray-400">Powierzchnia:</span>
                <span className="text-white ml-2">{precisionResult.details.A_mm2.toFixed(1)} mm²</span>
              </div>
            </div>
          </div>

          {/* Obliczenie liczby magnesów */}
          {loadWeight > 0 && (
            <div className="bg-zinc-800 rounded-lg p-4 mb-6">
              <h5 className="text-sm font-medium text-gray-400 mb-3">Wymagana liczba magnesów dla obciążenia {loadWeight} kg</h5>
              {(() => {
                const required = calculateRequiredMagnets(
                  loadWeight,
                  precisionResult.F_design,
                  precisionInput.safetyFactor,
                  'vertical'
                );
                return (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <span className="text-gray-400">Ilość magnesów:</span>
                      <p className="text-2xl font-bold text-white">{required.quantity} szt.</p>
                    </div>
                    <div>
                      <span className="text-gray-400">Całkowita siła:</span>
                      <p className="text-xl font-bold text-green-400">
                        {required.totalForce_N.toFixed(0)} N ({(required.totalForce_N / 9.81).toFixed(1)} kg)
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-400">Zapas bezpieczeństwa:</span>
                      <p className="text-xl font-bold text-blue-400">
                        {(required.totalForce_N / required.loadForce_N).toFixed(1)}x
                      </p>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Ostrzeżenia */}
          {precisionResult.warnings.length > 0 && (
            <div className="p-4 bg-yellow-500/20 rounded-lg mb-6">
              <h5 className="text-sm font-medium text-yellow-400 mb-2 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Ostrzeżenia
              </h5>
              <ul className="list-disc list-inside text-sm text-yellow-300 space-y-1">
                {precisionResult.warnings.map((warning, idx) => (
                  <li key={idx}>{warning}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Rekomendacja optymalnego gatunku */}
          <div className="p-4 bg-blue-500/20 rounded-lg">
            <h5 className="text-sm font-medium text-blue-400 mb-2 flex items-center gap-2">
              <Info className="w-4 h-4" />
              Rekomendacja gatunku
            </h5>
            {(() => {
              const recommendation = recommendOptimalGrade(
                {
                  shape: precisionInput.shape,
                  D: precisionInput.D,
                  L: precisionInput.L,
                  a: precisionInput.a,
                  b: precisionInput.b,
                  T: precisionInput.T,
                  airGap: precisionInput.airGap,
                  steelThickness: precisionInput.steelThickness,
                  orientation: precisionInput.orientation,
                  surface: precisionInput.surface,
                  safetyFactor: precisionInput.safetyFactor
                },
                {
                  minForce_N: loadWeight * 9.81 * precisionInput.safetyFactor,
                  preferHighTemp: precisionInput.T > 60
                }
              );
              return (
                <div className="text-sm text-gray-300">
                  <p className="mb-2">
                    <strong className="text-white">Rekomendowany:</strong> {recommendation.recommended}
                  </p>
                  <p className="mb-2">
                    <strong className="text-white">Powód:</strong> {recommendation.reason}
                  </p>
                  <p>
                    <strong className="text-white">Alternatywy:</strong> {recommendation.alternatives.join(', ')}
                  </p>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      {/* Porównanie gatunków */}
      {showComparison && comparisonResults && (
        <div className="bg-zinc-700 rounded-lg p-6">
          <h4 className="text-lg font-semibold text-white mb-4">Porównanie gatunków magnesów</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-600">
                  <th className="text-left py-2 px-3 text-gray-400">Gatunek</th>
                  <th className="text-right py-2 px-3 text-gray-400">Siła [N]</th>
                  <th className="text-right py-2 px-3 text-gray-400">Siła [kg]</th>
                  <th className="text-right py-2 px-3 text-gray-400">Zapas temp. [°C]</th>
                  <th className="text-right py-2 px-3 text-gray-400">Koszt względny</th>
                  <th className="text-right py-2 px-3 text-gray-400">Efektywność</th>
                </tr>
              </thead>
              <tbody>
                {comparisonResults.map((result, idx) => (
                  <tr key={idx} className="border-b border-zinc-700 hover:bg-zinc-600">
                    <td className="py-2 px-3 text-white font-medium">{result.grade}</td>
                    <td className="py-2 px-3 text-right text-gray-300">{result.force_N.toFixed(1)}</td>
                    <td className="py-2 px-3 text-right text-gray-300">{result.force_kg.toFixed(2)}</td>
                    <td className={`py-2 px-3 text-right ${
                      result.tempMargin_C < 0 ? 'text-red-400' : 
                      result.tempMargin_C < 20 ? 'text-yellow-400' : 
                      'text-green-400'
                    }`}>
                      {result.tempMargin_C > 0 ? '+' : ''}{result.tempMargin_C}
                    </td>
                    <td className="py-2 px-3 text-right text-gray-300">{result.cost_relative.toFixed(2)}</td>
                    <td className="py-2 px-3 text-right text-orange-400 font-medium">
                      {result.efficiency.toFixed(0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400 mt-3">
            * Efektywność = Siła / Koszt względny. Wyższy wynik = lepszy stosunek jakości do ceny.
          </p>
        </div>
      )}

      {/* Informacje techniczne */}
      <div className="bg-zinc-700 rounded-lg p-6">
        <h4 className="text-lg font-semibold text-white mb-4">Informacje techniczne</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
          <div>
            <h5 className="font-medium text-gray-400 mb-2">Wzory obliczeniowe:</h5>
            <ul className="space-y-1 text-gray-300">
              <li>• F₀ = (B²·A) / (2·μ₀)</li>
              <li>• B_gap = k_geo · Br(T)</li>
              <li>• Br(T) = Br₂₀ · [1 + α·(T-20°C)]</li>
              <li>• f_gap = 1 / (1 + gap/λ)²</li>
              <li>• f_steel = 1 - exp(-t/t_sat)</li>
            </ul>
          </div>
          <div>
            <h5 className="font-medium text-gray-400 mb-2">Stałe fizyczne:</h5>
            <ul className="space-y-1 text-gray-300">
              <li>• μ₀ = 4π×10⁻⁷ H/m</li>
              <li>• α_Br ≈ -0.11 to -0.12 %/°C</li>
              <li>• β_Hci ≈ -0.45 to -0.60 %/°C</li>
              <li>• μ_friction ≈ 0.3-0.6 (stal)</li>
            </ul>
          </div>
        </div>
        <div className="mt-4 p-3 bg-zinc-800 rounded text-xs text-gray-400">
          <p className="mb-1">Źródła danych: Arnold Magnetic Technologies, K&J Magnetics, IEC 60404-8-1, DIN EN 13155</p>
          <p>Model obliczeniowy zgodny z teorią Maxwella i standardami przemysłowymi.</p>
        </div>
      </div>
    </div>
  );
};
